import React, { useEffect, useState } from 'react';
import Pusher from 'pusher-js';
import AuthApi from '../Services/Authapi';
import './ChatBox.css';

const ChatBox = ({ currentUser: initialCurrentUser }) => {
  const [currentUser, setCurrentUser] = useState(initialCurrentUser);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [users, setUsers] = useState([]);
  const [to_id, setToId] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [newMessageNotification, setNewMessageNotification] = useState(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await AuthApi.getCurrentUser();
        if (response && response.data) {
          setCurrentUser(response.data);
        } else {
          console.error('Unexpected data format:', response);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await AuthApi.getData();
        if (response && Array.isArray(response.data)) {
          setUsers(response.data);
        } else {
          console.error('Unexpected data format:', response);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (!currentUser?.id) return;
  
    const pusher = new Pusher('d7e19ad35596f548da8e', {
      cluster: 'ap2',
      encrypted: true,
    });

    console.log('Pusher initialized');
  
    const channel = pusher.subscribe(`chatify_test.${currentUser.id}`);
        console.log('Subscribed to channel:', `chatify_test.${currentUser.id}`);
  
    channel.bind('message.sent', (data) => {
      console.log('Received message:', data);
      if (data.to_id === currentUser.id) {

          setMessages((prevMessages) => [
              ...prevMessages,
              data,
          ]);
          setNewMessage(true);
          setNewMessageNotification(true);
      }
  });
  
    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  }, [currentUser?.id]);
  

const fetchChatHistory = async (userId) => {
  try {
    const response = await AuthApi.getChatHistory(userId);
    if (response && Array.isArray(response.messages)) {
      setMessages(response.messages);
    } else {
      console.error('Unexpected data format:', response);
    }
  } catch (error) {
    console.error('Error fetching chat history:', error);
  }
};


  const handleUserSelect = (userId) => {
    setToId(userId);
    fetchChatHistory(userId);
    setUnreadCounts((prevCounts) => ({
      ...prevCounts,
      [userId]: 0,
    }));
  };

  const handleAttachmentChange = (e) => {
    setAttachment(e.target.files[0]);
  };

  const sendMessage = async () => {
    if (newMessage.trim() === '' && !attachment) return;
  
    try {
      const formData = new FormData();
      formData.append('message', newMessage);
      formData.append('to_id', to_id);
  
      if (attachment) {
        formData.append('attachment', attachment);
      }
  
      // Send message to the backend
      await AuthApi.sendMessage(formData);
  
      // Update messages for the sender
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          from_id: currentUser.id,
          body: newMessage,
          attachment: attachment ? URL.createObjectURL(attachment) : null,
          senderName: currentUser.firstName,
          timestamp: new Date().toISOString(),
          attachmentType: attachment ? attachment.type.split('/')[0] : null,
        },
      ]);
  
      // Reset input fields after sending
      setNewMessage('');
      setAttachment(null);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const handleReadMessages = () => {
  setNewMessage(false);
  setNewMessageNotification(false);
};

  return (
    <div className="chat-container">
      <div className="sidebar">
        <div className="user-list">
          {users.map((user) => (
            <div
              key={user.id}
              className={`user-item ${user.id === to_id ? 'active' : ''}`}
              onClick={() => handleUserSelect(user.id)}
            >
              {user.firstName}
              {unreadCounts[user.id] > 0 && (
                <span className="unread-count">{unreadCounts[user.id]}</span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="chat-box">
        <div className="chat-header">
          <h2>Chat with {to_id && users.find(user => user.id === to_id)?.firstName}</h2>
          {newMessageNotification && <span className="new-message-indicator">•</span>}
        </div>
        <div className="chat-history" onClick={handleReadMessages}>
          {messages.map((msg, index) => (
            <div key={index} className={`chat-message ${msg.from_id === to_id ? 'received' : 'sent'}`}>
              <div className="message-info">
                <strong>{msg.senderName}</strong> <span>{formatTimestamp(msg.timestamp)}</span>
              </div>
              <div className="message-body">
                {msg.body}
                {msg.attachment && (
                  <div className="attachment">
                    {msg.attachmentType === 'image' ? (
                      <img src={msg.attachment} alt="attachment" />
                    ) : (
                      <a href={msg.attachment} target="_blank" rel="noopener noreferrer">View Attachment</a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="message-input">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
          />
          <input
            type="file"
            onChange={handleAttachmentChange}
          />
          <button onClick={sendMessage}>Send</button>
        </div>
      </div>
    </div>
  );
};

export default ChatBox;